import { Link, useLocation } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCheckCircle, FiClock, FiHelpCircle, FiRefreshCw, FiShield, FiXCircle, FiZap } from "react-icons/fi";
import success from "../../Images/payment-success.png";
import failed from "../../Images/payment-failed.png";
import pending from "../../Images/payment-pending.png";
import { readRechargePaymentStatus } from "../../utils/rechargePayment";

const RechargePaymentStatus = () => {
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const savedStatus = readRechargePaymentStatus();

    const queryStatus = params.get("status");
    const status = queryStatus || savedStatus?.status || "failed";

    const isSuccess = status === "success";
    const isPending = status === "pending";
    const isFailed = !isSuccess && !isPending;

    const image = isSuccess ? success : isPending ? pending : failed;

    const title = isSuccess
        ? "Payment Successful"
        : isPending
            ? "Payment Pending"
            : "Payment Failed";

    const message =
        savedStatus?.message ||
        (isSuccess
            ? "Your payment was successful and the service request has been submitted."
            : isPending
                ? "Your payment is being processed. Please wait while we confirm your transaction."
                : "We could not complete this payment. Please check your details and try again.");

    const retryPath = savedStatus?.retryPath || "/";

    return (
        <main className="relative min-h-screen overflow-hidden bg-[#070614] px-4 py-8 text-white sm:px-6 lg:px-10">
            {/* Subtle page background */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_35%,rgba(112,67,196,0.12),transparent_32%),radial-gradient(circle_at_15%_80%,rgba(76,29,149,0.08),transparent_28%)]" />

            <div className="relative mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-6xl items-center justify-center">
                <section className="w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0d0b1d] shadow-2xl shadow-black/30">

                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-7">
                        <div className="flex items-center gap-2.5">
                            <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${isSuccess
                                    ? "bg-emerald-500/10 text-emerald-400"
                                    : isPending
                                        ? "bg-amber-500/10 text-amber-400"
                                        : "bg-rose-500/10 text-rose-400"
                                }`}>
                                {isSuccess ? <FiCheckCircle size={16} /> : isPending ? <FiClock size={16} /> : <FiXCircle size={16} />}
                            </div>
                            <div>
                                <p className="text-xs font-semibold tracking-wide text-white">NabhiVerse Payments</p>
                                <p className="text-[12px] text-slate-500">Payment status</p>
                            </div>
                        </div>

                        <div className="hidden items-center gap-1.5 text-[12px] text-slate-500 sm:flex">
                            <FiShield size={13} />
                            Secure transaction
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-[1.05fr_.95fr]">

                        {/* Details */}
                        <div className="px-5 py-8 sm:px-8 sm:py-10 lg:px-12">
                            <p className={`mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] ${isSuccess ? "text-emerald-400" : isPending ? "text-amber-400" : "text-rose-400"
                                }`}>
                                {isSuccess ? "Payment complete" : isPending ? "Payment in progress" : "Payment unsuccessful"}
                            </p>

                            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                                {title}
                            </h1>

                            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-400">
                                {message}
                            </p>

                            {/* Status notice */}
                            <div className={`mt-6 rounded-xl border p-4 ${isSuccess
                                    ? "border-emerald-500/20 bg-emerald-500/[0.06]"
                                    : isPending
                                        ? "border-amber-500/20 bg-amber-500/[0.06]"
                                        : "border-rose-500/20 bg-rose-500/[0.06]"
                                }`}>
                                <div className="flex gap-3">
                                    <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isSuccess ? "bg-emerald-500/10 text-emerald-400" : isPending ? "bg-amber-500/10 text-amber-400" : "bg-rose-500/10 text-rose-400"
                                        }`}>
                                        {isSuccess ? <FiCheckCircle size={17} /> : isPending ? <FiClock size={17} /> : <FiXCircle size={17} />}
                                    </div>

                                    <div>
                                        <p className="text-xs font-semibold text-white">
                                            {isSuccess
                                                ? "Payment has been confirmed"
                                                : isPending
                                                    ? "Payment is being verified"
                                                    : "Payment could not be completed"}
                                        </p>
                                        <p className="mt-1 text-xs leading-5 text-slate-500">
                                            {isSuccess
                                                ? "Your service request has been submitted successfully."
                                                : isPending
                                                    ? "Your payment provider may still be processing this transaction."
                                                    : "Please check your payment details and try again."}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Payment details */}
                            <div className="mt-5 divide-y divide-white/[0.06] rounded-xl border border-white/[0.07] bg-white/[0.02]">
                                {savedStatus?.serviceLabel && (
                                    <div className="flex items-center justify-between gap-4 px-4 py-3">
                                        <span className="text-xs text-slate-500">Service</span>
                                        <span className="text-xs font-medium text-slate-200">{savedStatus.serviceLabel}</span>
                                    </div>
                                )}

                                {savedStatus?.amount && (
                                    <div className="flex items-center justify-between gap-4 px-4 py-3">
                                        <span className="text-xs text-slate-500">Amount</span>
                                        <span className="text-sm font-semibold text-amber-300">Rs. {savedStatus.amount}</span>
                                    </div>
                                )}

                                {savedStatus?.requestId && (
                                    <div className="flex items-start justify-between gap-4 px-4 py-3">
                                        <span className="shrink-0 text-xs text-slate-500">Request ID</span>
                                        <code className="max-w-[65%] break-all text-right text-[10px] text-slate-400">{savedStatus.requestId}</code>
                                    </div>
                                )}

                                {savedStatus?.razorpayOrderId && (
                                    <div className="flex items-start justify-between gap-4 px-4 py-3">
                                        <span className="shrink-0 text-xs text-slate-500">Order ID</span>
                                        <code className="max-w-[65%] break-all text-right text-[10px] text-slate-400">{savedStatus.razorpayOrderId}</code>
                                    </div>
                                )}

                                {savedStatus?.razorpayPaymentId && (
                                    <div className="flex items-start justify-between gap-4 px-4 py-3">
                                        <span className="shrink-0 text-xs text-slate-500">Payment ID</span>
                                        <code className="max-w-[65%] break-all text-right text-[10px] text-slate-400">{savedStatus.razorpayPaymentId}</code>
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                {isSuccess ? (
                                    <Link
                                        to="/"
                                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-violet-500"
                                    >
                                        Go to Home
                                        <FiArrowRight size={16} />
                                    </Link>
                                ) : (
                                    <>
                                        <Link
                                            to={retryPath}
                                            className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold text-white transition ${isPending
                                                    ? "bg-amber-600 hover:bg-amber-500"
                                                    : "bg-violet-600 hover:bg-violet-500"
                                                }`}
                                        >
                                            <FiRefreshCw size={15} />
                                            Try Again
                                        </Link>

                                        <Link
                                            to="/"
                                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-5 py-3 text-sm font-medium text-slate-300 transition hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
                                        >
                                            <FiArrowLeft size={15} />
                                            Back to Home
                                        </Link>
                                    </>
                                )}
                            </div>

                            {/* Small trust row */}
                            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-slate-600">
                                <span className="inline-flex items-center gap-1.5">
                                    <FiShield className="text-teal-400" />
                                    Secure payments
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <FiRefreshCw className="text-teal-400" />
                                    Easy returns
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <FiHelpCircle className="text-teal-400" />
                                    24x7 support
                                </span>
                            </div>
                        </div>

                        {/* Visual */}
                        <div className="relative flex min-h-[330px] items-center justify-center border-t border-white/[0.06] bg-[#0a0918] px-8 lg:min-h-full lg:border-l lg:border-t-0">
                            <div className={`absolute h-56 w-56 rounded-full blur-3xl ${isSuccess ? "bg-emerald-500/10" : isPending ? "bg-amber-500/10" : "bg-violet-500/15"
                                }`} />

                            <div className="absolute inset-0 opacity-40">
                                <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full border border-violet-500/10" />
                                <div className="absolute left-1/2 top-1/2 h-52 w-52 -translate-x-1/2 -translate-y-1/2 rounded-full border border-violet-500/10" />
                            </div>

                            <div className="relative z-10">
                                <img
                                    src={image}
                                    alt={title}
                                    className="mx-auto w-48 object-contain drop-shadow-[0_18px_25px_rgba(0,0,0,0.45)] transition-transform duration-500 hover:scale-105 sm:w-56"
                                />

                                <div className={`mx-auto mt-5 flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-medium ${isSuccess
                                        ? "border-emerald-500/20 bg-emerald-500/[0.05] text-emerald-400"
                                        : isPending
                                            ? "border-amber-500/20 bg-amber-500/[0.05] text-amber-400"
                                            : "border-rose-500/20 bg-rose-500/[0.05] text-rose-400"
                                    }`}>
                                    {isSuccess ? <FiCheckCircle size={13} /> : isPending ? <FiClock size={13} /> : <FiXCircle size={13} />}
                                    {isSuccess ? "Payment verified" : isPending ? "Processing" : "Transaction declined"}
                                </div>
                            </div>

                            <FiZap className="absolute left-[15%] top-[25%] text-violet-400/50" size={17} />
                            <FiZap className="absolute bottom-[24%] right-[15%] text-fuchsia-400/40" size={14} />
                        </div>
                    </div>

                    {/* Support footer */}
                    <div className="border-t border-white/10 bg-white/[0.015] px-5 py-4 sm:px-7">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-2.5">
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 text-violet-300">
                                    <FiHelpCircle size={16} />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-slate-200">Need help?</p>
                                    <p className="text-[12px] text-slate-600">Our support team is available 24x7.</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-4 text-[12px] text-slate-500">
                                <span className="inline-flex items-center gap-1.5">
                                    <FiShield className="text-teal-400" /> Secure
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <FiRefreshCw className="text-teal-400" /> Easy refunds
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <FiHelpCircle className="text-teal-400" /> Support
                                </span>
                            </div>

                            <Link
                                to="/"
                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-violet-500/20 px-4 py-2 text-[10px] font-semibold text-violet-200 transition hover:border-violet-400/40 hover:bg-violet-500/5"
                            >
                                Contact Support
                                <FiArrowRight size={13} />
                            </Link>
                        </div>
                    </div>
                </section>

                <p className="absolute -bottom-7 left-0 text-[10px] text-slate-700">
                    © 2026 NabhiVerse. All Rights Reserved.
                </p>
            </div>
        </main>
    );
};

export default RechargePaymentStatus;